import { Typography, makeStyles } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import clsx from 'clsx'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AIIcon from 'resources/AIIcon'
import React from 'react'
import { SPEC_INT_AI_COLOR, SPEC_INT_MUTED_TEXT_COLOR } from './specIntelligenceConstants'

const useStyles = makeStyles(() => ({
    row: {
        display: 'flex',
        borderBottom: '1px solid #E0E0E0',
        flexShrink: 0,
    },
    tile: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${pxToRem(12)} ${pxToRem(8)}`,
        gap: pxToRem(2),
        '&:not(:last-child)': {
            borderRight: '1px solid #E0E0E0',
        },
    },
    iconRow: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
    },
    count: {
        fontSize: pxToRem(22),
        fontWeight: 700,
        lineHeight: 1,
    },
    label: {
        fontSize: pxToRem(10),
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase' as const,
        color: SPEC_INT_MUTED_TEXT_COLOR,
    },
    issuesIcon: { fontSize: pxToRem(21), color: '#616161' },
    issuesCount: { color: '#212121' },
    aiRecsIcon: { width: pxToRem(21), height: pxToRem(21), color: SPEC_INT_AI_COLOR },
    aiRecsCount: { color: SPEC_INT_AI_COLOR },
    resolvedIcon: { fontSize: pxToRem(21), color: '#2E7D32' },
    resolvedCount: { color: '#2E7D32' },
}))

interface StatTileProps {
    icon: React.ReactNode
    count: number
    label: string
    countClassName: string
}

const StatTile = ({ icon, count, label, countClassName }: StatTileProps) => {
    const classes = useStyles()
    return (
        <div className={classes.tile}>
            <div className={classes.iconRow}>
                {icon}
                <Typography className={clsx(classes.count, countClassName)}>{count}</Typography>
            </div>
            <Typography className={classes.label}>{label}</Typography>
        </div>
    )
}

/** Props for {@link SpecIntelligenceStatTiles}. */
export interface SpecIntelligenceStatTilesProps {
    /** Number of open findings. */
    openCount: number
    /** Number of open findings produced by AI-powered analysis. Decrements as AI findings are resolved/dismissed. */
    aiCount: number
    /** Number of resolved or dismissed findings. */
    resolvedCount: number
}

/**
 * Three-tile summary row for a SpecIntelligence report: ISSUES, AI RECS, RESOLVED.
 */
const SpecIntelligenceStatTiles = ({ openCount, aiCount, resolvedCount }: SpecIntelligenceStatTilesProps) => {
    const classes = useStyles()
    return (
        <div className={classes.row} role="region" aria-label="Report summary">
            <StatTile
                icon={<ErrorOutlineIcon className={classes.issuesIcon} aria-hidden="true" />}
                count={openCount}
                label="Issues"
                countClassName={classes.issuesCount}
            />
            <StatTile
                icon={<AIIcon className={classes.aiRecsIcon} aria-hidden="true" />}
                count={aiCount}
                label="AI Recs"
                countClassName={classes.aiRecsCount}
            />
            <StatTile
                icon={<CheckCircleOutlineIcon className={classes.resolvedIcon} aria-hidden="true" />}
                count={resolvedCount}
                label="Resolved"
                countClassName={classes.resolvedCount}
            />
        </div>
    )
}

export default SpecIntelligenceStatTiles
